import { Component } from '@theme/component';
import { createSpinMeasurement } from './nspire-spin-measurement.js';

if (!customElements.get('nspire-army-spin')) {
  customElements.define('nspire-army-spin', class extends Component {
    connectedCallback() {
      super.connectedCallback();
      this.stop?.();
      this.measurement?.disconnect();
      this.controller?.abort();
      this.controller = new AbortController();
      const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: this.controller.signal });
      const trigger = this.querySelector('.nspire-spin-trigger');
      const dialog = this.querySelector('dialog');
      const close = this.querySelector('.nspire-spin-close');
      const stage = this.querySelector('.nspire-spin-stage');
      const video = this.querySelector('video');
      const status = this.querySelector('[role="status"]');
      if (!trigger || !dialog || !close || !stage || !video || !status || !dialog.showModal) return;
      const measurement = this.measurement = createSpinMeasurement(this, { trigger, dialog, video });
      const total = Number(video.dataset.frames) || 202, fps = Number(video.dataset.fps) || 24;
      let mediaPromise;
      let frame = Number(stage.dataset.frame || 0), ready = video.readyState >= 2, pointer = null, startX = 0, startFrame = 0, raf = 0, overflow = '';
      const seek = () => {
        raf = 0;
        if (!ready || video.seeking || !dialog.open) return;
        const time = Math.min(frame / fps, Math.max(0, video.duration - 1 / fps));
        if (Math.abs(video.currentTime - time) > .005) video.currentTime = time;
      };
      const schedule = () => { if (!raf) raf = requestAnimationFrame(seek); };
      const setFrame = value => {
        const previous = frame;
        frame = ((Math.round(value) % total) + total) % total;
        stage.dataset.frame = String(frame);
        if (frame !== previous) measurement.interacted();
        schedule();
      };
      const finish = () => {
        const previous = pointer;
        pointer = null;
        delete stage.dataset.dragging;
        if (previous !== null && stage.hasPointerCapture(previous)) stage.releasePointerCapture(previous);
      };
      listen(trigger, 'click', async () => {
        if (dialog.open) return;
        overflow = document.documentElement.style.overflow;
        dialog.showModal();
        measurement.opened();
        measurement.loaded();
        document.documentElement.style.overflow = 'hidden';
        close.focus({preventScroll:true});
        if (!video.getAttribute('src')) {
          status.textContent = status.dataset.loading;
          try {
            mediaPromise ||= import(video.dataset.src);
            const media = await mediaPromise;
            if (!dialog.open || !this.isConnected) return;
            video.src = media.default;
            video.load();
          } catch {
            mediaPromise = undefined;
            status.textContent = status.dataset.error;
          }
        }
        schedule();
      });
      listen(close, 'click', () => dialog.close());
      listen(dialog, 'keydown', event => {
        if (event.key !== 'Tab') return;
        if (event.shiftKey && document.activeElement === close) {
          event.preventDefault();
          stage.focus();
        } else if (!event.shiftKey && document.activeElement === stage) {
          event.preventDefault();
          close.focus();
        }
      });
      let backdrop = false;
      listen(dialog, 'pointerdown', event => {
        const r = dialog.getBoundingClientRect();
        backdrop = event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom);
      });
      listen(dialog, 'click', event => {
        const r = dialog.getBoundingClientRect();
        if (backdrop && event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) dialog.close();
        backdrop = false;
      });
      listen(dialog, 'close', () => {
        finish();
        video.pause();
        document.documentElement.style.overflow = overflow;
        if (trigger.isConnected) trigger.focus({preventScroll:true});
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
      });
      listen(video, 'loadeddata', () => { ready = true; video.pause(); status.textContent = ''; measurement.loaded(); schedule(); });
      listen(video, 'seeked', schedule);
      listen(video, 'error', () => { ready = false; video.removeAttribute('src'); status.textContent = status.dataset.error; });
      listen(stage, 'pointerdown', event => {
        if (!ready || pointer !== null || !event.isPrimary || event.button !== 0) return;
        pointer = event.pointerId;
        startX = event.clientX;
        startFrame = frame;
        stage.focus({preventScroll:true});
        stage.setPointerCapture(pointer);
        stage.dataset.dragging = '';
      });
      listen(stage, 'pointermove', event => {
        if (pointer === null || event.pointerId !== pointer) return;
        setFrame(startFrame + (event.clientX - startX) / Math.max(280, stage.clientWidth) * total);
      });
      ['pointerup','pointercancel','lostpointercapture'].forEach(name => listen(stage, name, event => { if (event.pointerId === pointer) finish(); }));
      listen(stage, 'keydown', event => {
        if (!ready) return;
        const step = event.shiftKey ? 10 : 1;
        if (event.key === 'ArrowLeft') setFrame(frame - step);
        else if (event.key === 'ArrowRight') setFrame(frame + step);
        else if (event.key === 'Home') setFrame(0);
        else if (event.key === 'End') setFrame(total - 1);
        else return;
        event.preventDefault();
      });
      trigger.disabled = false;
      measurement.available();
      this.stop = () => {
        finish();
        if (dialog.open) {
          dialog.close();
          document.documentElement.style.overflow = overflow;
        }
        video.pause();
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
      };
    }
    updatedCallback() {
      super.updatedCallback();
      this.connectedCallback();
    }
    disconnectedCallback() {
      this.stop?.();
      this.measurement?.disconnect();
      this.controller?.abort();
      super.disconnectedCallback();
    }
  });
}
